import { useCallback, useRef, useEffect } from 'react';
import { parseAIResponse } from '../utils/aiResponseParser';
import { handleFormLogic } from '../utils/formLogicHandler';
import { useFormContext } from '../contexts/FormContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useChatSession } from './useChatSession';
import { ChatFormParser } from '../utils/chatFormParser';
import { logger } from '../utils/logger';
import SecureStorage from '../utils/secureStorage';
import { AutoLeadSubmission } from '../utils/autoLeadSubmission';
import { sessionManager } from '../utils/sessionManager';

export const useChatFormLogic = (setIsExpanded?: (expanded: boolean) => void) => {
  const { setActiveForm, activateOfferForm, closeForm } = useFormContext();
  const { setLanguage } = useLanguage();
  const { addMessage } = useChatSession();
  const holdAIResponseRef = useRef(false);
  const pendingAIResponseRef = useRef<string | null>(null);

  // Listen for hold/release events from WaitingVideoMessage form
  useEffect(() => {
    const handleHoldEvent = (event: CustomEvent) => {
      const shouldHold = event.detail?.hold === true;
      holdAIResponseRef.current = shouldHold;
      
      logger.log(`🔒 AI Response ${shouldHold ? 'HELD' : 'RELEASED'}`);
      
      // If released and we have a pending response, display it now
      if (!shouldHold && pendingAIResponseRef.current) {
        const pendingResponse = pendingAIResponseRef.current;
        pendingAIResponseRef.current = null;
        logger.log('📤 Displaying held AI response now');
        
        // Add the message immediately
        try {
          addMessage(pendingResponse, 'Dealer');
          logger.log('✅ Successfully added previously held AI message');
        } catch (error) {
          logger.error('❌ Error adding held message:', error);
        }
      }
    };

    window.addEventListener('d2c-hold-ai-response', handleHoldEvent as EventListener);
    return () => window.removeEventListener('d2c-hold-ai-response', handleHoldEvent as EventListener);
  }, [addMessage]);

  // Check if there was a recent form submission within 2 minutes
  const hasRecentFormSubmission = useCallback(async (): Promise<boolean> => {
    try {
      const lastFormSubmissionTime = await SecureStorage.getItem('lastFormSubmissionTime');
      if (!lastFormSubmissionTime) return false;
      
      const timeDiff = Date.now() - lastFormSubmissionTime;
      const tenSecondsInMs = 10 * 1000; // 10 seconds (reduced from 2 minutes)
      
      if (timeDiff < tenSecondsInMs) {
        logger.log(`⏰ Recent form submission detected ${Math.round(timeDiff / 1000)}s ago - brief cooldown active`);
        return true;
      }
      
      return false;
    } catch (error) {
      logger.error('❌ Error checking recent form submission:', error);
      return false;
    }
  }, []);

  const processAIResponse = useCallback(async (aiResponse: string) => {
    logger.log('🔍 Processing AI response for forms and language switching');
    
    // CRITICAL: Check for Language field in raw response
    
    // Use the refactored parser for cleaner form detection
    let parsedResponse = parseAIResponse(aiResponse);
    
    // Fallback to the new ChatFormParser if no response
    if (!parsedResponse) {
      const formResponse = ChatFormParser.parseFormResponse(aiResponse);
      if (formResponse) {
        parsedResponse = formResponse as any; // Type compatibility bridge
      }
    }
    
    if (parsedResponse) {
      logger.log('🔥🔥🔥 INSIDE PARSED RESPONSE BLOCK');
      
      // CRITICAL: If Contact exists but no Form, auto-create Contact form
      if (parsedResponse.Contact && !parsedResponse.Form) {
        logger.log('🔥🔥🔥 CONTACT EXISTS WITHOUT FORM - AUTO-CREATING CONTACT FORM');
        parsedResponse.Form = {
          Type: 'Contact',
          Title: 'Nous contacter',
          Contact: parsedResponse.Contact
        };
      }
      
      // Auto-lead submission now handled in useChat.ts
      
      // Check for recent form submission before processing Form objects
      const hasRecent = await hasRecentFormSubmission();
      if (hasRecent) {
        logger.log('🚫 Ignoring Form object from OpenAI due to recent form submission (within 2 minutes)');
        
        // Still add the message for display but skip form processing (or hold it)
        if (holdAIResponseRef.current) {
          logger.log('🔒 Holding AI response - user is typing in form');
          pendingAIResponseRef.current = aiResponse;
        } else {
          try {
            addMessage(aiResponse, 'Dealer');
            logger.log('✅ Added response as regular message without form processing due to recent submission');
          } catch (error) {
            logger.error('❌ Error adding message:', error);
          }
        }
        return;
      }
      
      logger.log('✅ Valid parsed response found, calling handleFormLogic');
      
      handleFormLogic(
        parsedResponse,
        null,
        setActiveForm,
        activateOfferForm,
        closeForm,
        setLanguage,
        false,
        setIsExpanded,
        Date.now() // Pass current timestamp for new messages
      );
      
      // Add the AI response as a chat message for display (or hold it)
      if (holdAIResponseRef.current) {
        logger.log('🔒 Holding AI response - user is typing in form');
        pendingAIResponseRef.current = aiResponse;
      } else {
        try {
          addMessage(aiResponse, 'Dealer');
          logger.log('✅ Successfully added AI message to chat');
        } catch (error) {
          logger.error('❌ Error adding message:', error);
        }
      }
    } else {
      logger.log('⚠️ No Form.Type detected in AI response');
      
      // Still add the message for display (or hold it)
      if (holdAIResponseRef.current) {
        logger.log('🔒 Holding AI response - user is typing in form');
        pendingAIResponseRef.current = aiResponse;
      } else {
        try {
          addMessage(aiResponse, 'Dealer');
          logger.log('✅ Added response as regular message without form processing');
        } catch (error) {
          logger.error('❌ Error adding message:', error);
        }
      }
    }
  }, [setActiveForm, activateOfferForm, closeForm, setLanguage, addMessage, hasRecentFormSubmission, setIsExpanded]);

  return {
    processAIResponse,
  };
};